import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingEntryDA } from '../../services/admin/accounting.entry.service';
import { Error } from '../../models/error.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Company } from '../../models/company.model';
import { AccountingEntry } from '../../models/accounting.entry.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { Names } from '../../models/names.model';
import { Message } from '../../models/message.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Debit } from '../../models/debit.model';
import { Credit } from '../../models/credit.model';
import { Settings } from '../../models/settings.model';
import { Tax } from '../../models/tax.model';
import { taxFeeList } from '../../scripts/list.filter.script';
import { legendInvalid, legendIconInvalid, legendValidInvalidRestart, legendIconValidInvalidRestart, legendValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { getError, getMessage } from '../../languages/translations/response';
import { accountingAccountSelected } from '../../scripts/list.selected.script';
import { paymentMethodValue } from '../../libraries/payment.library';
import { formatAccountingAccount, formatMoney } from '../../scripts/format.value.script';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { uploadTooltip } from '../../utils/tooltip.util';
import { accountingEntryCreditTypeList, accountingEntryCreditTypeValue, accountingEntryDebitTypeList, accountingEntryDebitTypeValue } from '../../libraries/accounting.entry.library';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { taxPurchaseRecoverableCodes, taxPurchaseReverseChargeCodes } from '../../libraries/tax.code.library';

import AppLoading from '../Loading';
import AppLegend from '../element/Legend';
import AppInputTreasuryAccount from '../element/InputTreasuryAccount';
import AppInputPaymentMethod from '../element/InputPaymentMethod';
import AppInputAccountingEntry from '../element/InputAccountingEntry';

export interface AppModalContactCreateAccountingEntryProps {
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  accountingAccountsForCompany: AccountingAccount[] | null,
  accountingCreditEntriesForCompany: AccountingEntry[] | null,
  accountingDebitEntriesForCompany: AccountingEntry[] | null,
  treasuryAccountsForCompany: TreasuryAccount[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  accountingTaxAccountsForCompany: AccountingAccount[] | null,
  transaction: string | null,
  contact: string | undefined | null,
  loadAccountingCreditEntriesForCompany: Function,
  loadAccountingDebitEntriesForCompany: Function,
  loadAccountingContactEntries: Function
};

let errorResponse: Error, messageResponse: Message;

const AppModalContactCreateAccountingEntry: React.FunctionComponent<AppModalContactCreateAccountingEntryProps> = ({companyForUser, settingsForCompany, accountingAccountsForCompany, accountingCreditEntriesForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, accountingExpenseAccountsForCompany, accountingIncomeAccountsForCompany, accountingTaxAccountsForCompany, transaction, contact, loadAccountingCreditEntriesForCompany, loadAccountingDebitEntriesForCompany, loadAccountingContactEntries}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [subtype, setSubype] = useState({value: '', valid: false})
  const [amount, setAmount] = useState({value: '', valid: false})
  const [treasuryAccount, setTreasuryAccount] = useState({value: '', valid: false, object: null as any})
  const [conceptAccount, setConceptAccount] = useState({value: '', valid: false, object: null as any})
  const [taxFee, setTaxFee] = useState({value: '', valid: false, object: null as any})
  const [taxAccount, setTaxAccount] = useState({value: '', valid: false, object: null as any})
  const [method, setMethod] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false, starting: true})
  const [accountingEntry, setAccountingEntry] = useState({value: '', valid: false, object: null as any})

  const submitAccountingEntryCreate = async () => {
    setLoadIndicator('on')

    let isValidTransaction: boolean = accountingEntry.valid

    if (!isValidTransaction) {
      if (subtype.value === 'interest' || subtype.value === 'penalty') {
        isValidTransaction = amount.valid && date.valid
      }
      if (subtype.value === 'refund' || subtype.value === 'payment') {
        isValidTransaction = amount.valid && date.valid && treasuryAccount.valid
      }
      if (transaction === 'credit' && subtype.value === 'credit-note') {
        isValidTransaction = amount.valid && date.valid && conceptAccount.valid && taxFee.valid
      }
      if (companyForUser && !settingsForCompany?.subject_tax && transaction === 'debit' && subtype.value === 'credit-note') {
        isValidTransaction = amount.valid && date.valid && conceptAccount.valid
      }
      if (companyForUser && settingsForCompany?.subject_tax && transaction === 'debit' && subtype.value === 'credit-note') {
        if ((settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === "effective" && (taxPurchaseRecoverableCodes().includes(taxFee.value) || taxPurchaseReverseChargeCodes().includes(taxFee.value))) {
          isValidTransaction = amount.valid && date.valid && conceptAccount.valid && taxFee.valid && taxAccount.valid
        } else {
          isValidTransaction = amount.valid && date.valid && conceptAccount.valid && taxFee.valid
        }
      }
    }

    if (companyForUser && transaction && contact && subtype.valid && isValidTransaction) {
      if (accountingEntry.valid) {
        await AccountingEntryDA.putAccountingEntry(companyForUser.id, accountingEntry.value, contact, subtype.value).then( (response) => {
          if (response.status === 200) {
            messageResponse = response.data

            loadAccountingCreditEntriesForCompany(companyForUser.id)
            loadAccountingDebitEntriesForCompany(companyForUser.id)
            loadAccountingContactEntries(companyForUser.id, contact)

            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              executeHideModalPaymentCreate()
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
      } else {
        let company = {subject_tax: settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false, tax_method: settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective', tax_declaration: settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? 'invoicing', tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}
        let taxCode = taxFee.object?.code ?? ''
        let taxRate = taxFee.object?.rate ?? 0

        await AccountingEntryDA.postAccountingTransactionEntry(companyForUser.id, transaction, contact, subtype.value, treasuryAccount.value, conceptAccount.value, taxCode, taxRate, taxAccount.value, method.value, date.value, amount.value, company).then( (response) => {
          if (response.status === 201) {
            messageResponse = response.data

            loadAccountingCreditEntriesForCompany(companyForUser.id)
            loadAccountingDebitEntriesForCompany(companyForUser.id)
            loadAccountingContactEntries(companyForUser.id, contact)

            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              executeHideModalPaymentCreate()
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!subtype.valid) {
          legendInvalid('modal-contact-create-accounting-entry-legend-subtype-required')
        } else {
          if (subtype.value === 'interest' || subtype.value === 'penalty') {
            if (!amount.valid) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
            if (!date.valid) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
          }
          if (subtype.value === 'refund' || subtype.value === 'payment') {
            if (!accountingEntry.value.length && !amount.value.length) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
            if (!accountingEntry.value.length && !date.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
            if (!accountingEntry.value.length && !treasuryAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-treasury-account-required')
            if (!accountingEntry.value.length && !amount.value.length && !treasuryAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-accounting-entry-required')
          }
          if (transaction === 'credit' && subtype.value === 'credit-note') {
            if (!accountingEntry.value.length && !amount.value.length) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
            if (!accountingEntry.value.length && !date.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
            if (!accountingEntry.value.length && !conceptAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-concept-account-required')
            if (!accountingEntry.value.length && !taxFee.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-tax-fee-required')
            if (!accountingEntry.value.length && !amount.value.length && !conceptAccount.value.length && !taxFee.value) legendInvalid('modal-contact-create-accounting-entry-legend-accounting-entry-required')
          }
          if (companyForUser && !settingsForCompany?.subject_tax && transaction === 'debit' && subtype.value === 'credit-note') {
            if (!accountingEntry.value.length && !amount.value.length) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
            if (!accountingEntry.value.length && !date.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
            if (!accountingEntry.value.length && !conceptAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-concept-account-required')
            if (!accountingEntry.value.length && !amount.value.length && !conceptAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-accounting-entry-required')
          }
          if (companyForUser && settingsForCompany?.subject_tax && transaction === 'debit' && subtype.value === 'credit-note') {
            if ((settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === "effective" && (taxPurchaseRecoverableCodes().includes(taxFee.value) || taxPurchaseReverseChargeCodes().includes(taxFee.value))) {
              if (!accountingEntry.value.length && !amount.value.length) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
              if (!accountingEntry.value.length && !date.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
              if (!accountingEntry.value.length && !conceptAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-concept-account-required')
              if (!accountingEntry.value.length && !taxFee.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-tax-fee-required')
              if (!accountingEntry.value.length && !taxAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-tax-account-required')
              if (!accountingEntry.value.length && !amount.value.length && !conceptAccount.value.length && !taxFee.value && !taxAccount.value) legendInvalid('modal-contact-create-accounting-entry-legend-accounting-entry-required')
            } else {
              if (!accountingEntry.value.length && !amount.value.length) legendIconInvalid('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
              if (!accountingEntry.value.length && !date.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
              if (!accountingEntry.value.length && !conceptAccount.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-concept-account-required')
              if (!accountingEntry.value.length && !taxFee.value.length) legendInvalid('modal-contact-create-accounting-entry-legend-tax-fee-required')
              if (!accountingEntry.value.length && !amount.value.length && !conceptAccount.value.length && !taxFee.value) legendInvalid('modal-contact-create-accounting-entry-legend-accounting-entry-required')
            }
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalPaymentCreate = () => {
    modalHide('modal-contact-create-accounting-entry')

    setTimeout( () => {
      restartModal('none')
      setSubype({value: '', valid: false})
      legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-subtype-required')
    }, 200 )
  }

  const handleChangeAmount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAmount({...amount, value: event.target.value})
    setAccountingEntry({value: '', valid: false, object: null})

    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-accounting-entry-required')
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({...date, value: event.target.value, valid: true})
      legendValid('modal-contact-create-accounting-entry-legend-date-required')
    } else {
      setDate({...date, value: event.target.value, valid: false})
      legendInvalid('modal-contact-create-accounting-entry-legend-date-required')
    }

    setAccountingEntry({value: '', valid: false, object: null})
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-accounting-entry-required')
  }

  const handleChangeConceptAccount = (item: AccountingAccount) => {
    setConceptAccount({value: item.id, valid: true, object: item})
    legendValid('modal-contact-create-accounting-entry-legend-concept-account-required')
  }

  const handleChangeTaxAccount = (item: AccountingAccount) => {
    setTaxAccount({value: item.id, valid: true, object: item})
    legendValid('modal-contact-create-accounting-entry-legend-tax-account-required')
  }

  const handleChangeTaxFee = (item: Tax) => {
    setTaxFee({value: item.code, valid: true, object: item})
    legendValid('modal-contact-create-accounting-entry-legend-tax-fee-required')

    if (!(taxPurchaseRecoverableCodes().includes(item.code) || taxPurchaseReverseChargeCodes().includes(item.code))) {
      setTaxAccount({value: '', valid: false, object: null})
      legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-tax-account-required')
    }
  }

  const handleChangeType = (item: string) => {
    setSubype({value: item, valid: true})
    legendValid('modal-contact-create-accounting-entry-legend-subtype-required')

    restartModal(item)
  }

  const validateAmount = () => {
    evaluateLegendRequiredIcon(expressions.currency, amount, setAmount, 'modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-valid', 'modal-contact-create-accounting-entry-legend-amount-required')
  }

  function accountingEntryType(code: string): string {
    switch (transaction) {
      case 'debit':
        return accountingEntryDebitTypeValue(lang, code)
      case 'credit':
        return accountingEntryCreditTypeValue(lang, code)
      default:
        return lang.labels.undefined
    }
  }

  function accountingEntryTypes(): {code: string, display: string}[] {
    switch (transaction) {
      case 'debit':
        return accountingEntryDebitTypeList(lang)
      case 'credit':
        return accountingEntryCreditTypeList(lang)
      default:
        return []
    }
  }

  function accountingEntries(): AccountingEntry[] {
    switch (transaction) {
      case 'debit':
        return accountingDebitEntriesForCompany ?? []
      case 'credit':
        return accountingCreditEntriesForCompany ?? []
      default:
        return []
    }
  }

  function selectDebitCredit(): Debit | Credit {
    switch (transaction) {
      case 'debit':
        return accountingEntry.object.credits[0]
      case 'credit':
        return accountingEntry.object.debits[0]
      default:
        throw new globalThis.Error(lang.labels.undefined)
    }
  }

  function datePayment(): boolean {
    switch (true) {
      case (date.starting && !date.valid && date.value === ''):
        setDate({value: format((new Date()), 'yyyy-MM-dd'), valid: true, starting: false})
        return true
      case (date.valid && date.value.length > 0):
        return true
      default:
        return false
    }
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  function accountingAccountList(): AccountingAccount[] {
    switch (transaction) {
      case 'debit':
        return accountingExpenseAccountsForCompany || []
      case 'credit':
        return accountingIncomeAccountsForCompany || []
      default:
        return []
    }
  }

  function restartModal(sub_type: string) {
    legendIconValidInvalidRestart('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-valid')
    legendIconValidInvalidRestart('modal-contact-create-accounting-entry-input-amount', 'modal-contact-create-accounting-entry-legend-amount-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-treasury-account-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-concept-account-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-tax-fee-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-tax-account-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-payment-method-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-date-required')
    legendValidInvalidRestart('modal-contact-create-accounting-entry-legend-accounting-entry-required')

    setAmount({value: '', valid: false})
    setMethod({value: '', valid: false})
    setDate({value: '', valid: false, starting: true})
    setAccountingEntry({value: '', valid: false, object: null})

    if (treasuryAccountsForCompany && treasuryAccountsForCompany.length > 0 && (sub_type === 'refund' || sub_type === 'payment')) {
      setTreasuryAccount({value: treasuryAccountsForCompany[0].id, valid: true, object: treasuryAccountsForCompany[0]})
    } else {
      setTreasuryAccount({value: '', valid: false, object: null})
    }

    if (accountingExpenseAccountsForCompany && transaction === 'debit' && sub_type === 'credit-note') {
      let accountingAccount = accountingExpenseAccountsForCompany.find((item) => (item.code === '4200')) as AccountingAccount
      setConceptAccount({value: accountingAccount.id, valid: true, object: accountingAccount})
    } else if (accountingIncomeAccountsForCompany && transaction === 'credit' && sub_type === 'credit-note') {
      let accountingAccount = accountingIncomeAccountsForCompany.find((item) => (item.code === '3400')) as AccountingAccount
      setConceptAccount({value: accountingAccount.id, valid: true, object: accountingAccount})
    } else {
      setConceptAccount({value: '', valid: false, object: null})
    }

    if (companyForUser && settingsForCompany && transaction && settingsForCompany?.subject_tax && taxFeeList(transaction, date.value, settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []).length > 0 && sub_type === 'credit-note') {
      let tax = taxFeeList(transaction, date.value, settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? [])[0] as Tax
      setTaxFee({value: tax.code, valid: true, object: tax})

      if (accountingTaxAccountsForCompany && (settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === 'effective' && transaction === 'debit' && accountingTaxAccountsForCompany.length > 0 && (taxPurchaseRecoverableCodes().includes(tax.code) || taxPurchaseReverseChargeCodes().includes(tax.code))) {
        setTaxAccount({value: accountingTaxAccountsForCompany[0].id, valid: true, object: accountingTaxAccountsForCompany[0]})
      } else {
        setTaxAccount({value: '', valid: false, object: null})
      }
    } else {
      setTaxFee({value: '', valid: false, object: null})
      setTaxAccount({value: '', valid: false, object: null})
    }
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, accountingAccountsForCompany, accountingCreditEntriesForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany])

  return (
    <div id="modal-contact-create-accounting-entry" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{transaction ? lang.labels[`createClient${transaction.charAt(0).toUpperCase()}${transaction.slice(1)}` as keyof typeof lang.labels] : lang.labels.undefined}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && settingsForCompany && accountingAccountsForCompany && accountingCreditEntriesForCompany && accountingDebitEntriesForCompany && treasuryAccountsForCompany && accountingExpenseAccountsForCompany && accountingIncomeAccountsForCompany && accountingTaxAccountsForCompany && transaction
              ?
              <div className="form">
                <div className="mx-2 mb-5">
                  <div className="row mb-3">
                    <label className="fw-bolder">{lang.labels.transactionType}</label>
                    <span className="text-muted text-wrap">{lang.labels.selectOptionToEnableForm}</span>
                  </div>
                  <div className="w-100">
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {subtype.valid ?  accountingEntryType(subtype.value) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { accountingEntryTypes().map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === subtype.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === subtype.value}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeType(item.code)}>{item.display}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: subtype.valid, name: "subtype", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
                <div className="d-flex flex-column flex-xl-row">
                  <fieldset className={`col-xl-6 mb-5 mb-xl-0 ${!subtype.valid && "opacity-50"}`} disabled={!subtype.valid}>
                    <div className="h-100 m-0">
                      <div className="border border-dashed border-gray-300 rounded bg-lighten h-100 p-5">
                        <h6 className="text-dark text-center text-uppercase mb-3">{lang.labels.enterNewTransaction}</h6>
                        <div className="fv-row mb-3">
                          <label className="form-label">
                            <div className="d-flex align-items-center">
                              <span className="required">{lang.labels.depositAmount}</span>
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.amountMoneyEnteredInTransaction}><FaExclamationCircle /></i>
                            </div>
                          </label>
                          <div className="position-relative">
                            <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder text-center w-60px h-auto px-2">{companyForUser.currency?.symbol}</div>  
                            <input id="modal-contact-create-accounting-entry-input-amount" className="form-control ps-20" type="text" autoComplete="off" name="amount" placeholder="0.00" value={amount.value} onChange={handleChangeAmount} onKeyUp={validateAmount} onBlur={validateAmount} />
                          </div>
                          <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: amount.valid, name: "amount", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                        <div className={`fv-row mb-3 ${!(subtype.valid && (subtype.value === "refund" || subtype.value === "payment")) && "d-none"}`}>
                          <label className="form-label">
                            <div className="d-flex align-items-center">
                              <span className="required">{lang.labels.treasuryAccount}</span>
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.sourceTheAmount}><FaExclamationCircle /></i>
                            </div>
                          </label>
                          <AppInputTreasuryAccount component={"modal-contact-create-accounting-entry"} solid={false} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount} setAccountingEntry={setAccountingEntry}></AppInputTreasuryAccount>
                        </div>
                        <div className={`fv-row mb-3 ${!(subtype.valid && subtype.value === "credit-note") && "d-none"}`}>
                          <label className="form-label">
                            <div className="d-flex align-items-center">
                              <span className="required">{lang.labels.conceptAccount}</span>
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.accountingAccountProductService}><FaExclamationCircle /></i>
                            </div>
                          </label>
                          <Dropdown>
                            <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                              <span className="selection">
                                <span className="select2-selection select2-selection--single form-select">
                                  <span role="textbox">
                                    {conceptAccount.valid ? formatAccountingAccount(conceptAccount.object, companyForUser.language) : lang.labels.selectOption}
                                  </span>
                                </span>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                              <span className="select2-dropdown select2-dropdown--below">
                                <span className="select2-results">
                                  <ul className="select2-results__options" role="listbox">
                                    { accountingAccountList().map (( (item, index) => { return (
                                      <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === conceptAccount.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === conceptAccount.value}>
                                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeConceptAccount(item)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                                      </li>
                                    )}))}
                                  </ul>
                                </span>
                              </span>
                            </Dropdown.Menu>
                          </Dropdown>
                          <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: conceptAccount.valid, name: "concept-account", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                        <div className={`fv-row mb-3 ${!(subtype.valid && subtype.value === "credit-note" && ((transaction === "credit") || (transaction === "debit" && settingsForCompany?.subject_tax))) && "d-none"}`}>
                          <label className="form-label required">{lang.labels.taxFee}</label>
                          <Dropdown>
                            <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                              <span className="selection">
                                <span className="select2-selection select2-selection--single form-select">
                                  <span role="textbox">
                                    {taxFee.valid ? taxFee.object.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names] : lang.labels.selectOption}
                                  </span>
                                </span>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                              <span className="select2-dropdown select2-dropdown--below">
                                <span className="select2-results">
                                  <ul className="select2-results__options" role="listbox">
                                    { taxFeeList(transaction, date.value, settingsForCompany?.subject_tax ?? false, settingsForCompany?.tax_fees ?? []).map (( (item, index) => { return (
                                      <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === taxFee.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === taxFee.value}>
                                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxFee(item)}>{item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                                      </li>
                                    )}))}
                                  </ul>
                                </span>
                              </span>
                            </Dropdown.Menu>
                          </Dropdown>
                          <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: taxFee.valid, name: "tax-fee", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                        <div className={`fv-row mb-3 ${!(subtype.valid && subtype.value === "credit-note" && transaction === "debit" && settingsForCompany?.subject_tax && (settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === "effective" && (taxPurchaseRecoverableCodes().includes(taxFee.value) || taxPurchaseReverseChargeCodes().includes(taxFee.value))) && "d-none"}`}>
                          <label className="form-label required">{lang.labels.vatAccount}</label>
                          <Dropdown>
                            <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                              <span className="selection">
                                <span className="select2-selection select2-selection--single form-select">
                                  <span className="select2-selection__rendered" role="textbox">
                                    {taxAccount.valid ? formatAccountingAccount(taxAccount.object, companyForUser.language) : lang.labels.selectOption}
                                  </span>
                                </span>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                              <span className="select2-dropdown select2-dropdown--below">
                                <span className="select2-results">
                                  <ul className="select2-results__options" role="listbox">
                                    { accountingTaxAccountsForCompany.map (( (item, index) => { return (
                                      <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === taxAccount.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === taxAccount.value}>
                                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxAccount(item)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                                      </li>
                                    )}))}
                                  </ul>
                                </span>
                              </span>
                            </Dropdown.Menu>
                          </Dropdown>
                          <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: taxAccount.valid, name: "tax-account", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 fv-row mb-3 mb-lg-0 d-none">
                            <label className="form-label required">{lang.labels.paymentMethod}</label>
                            <AppInputPaymentMethod component={"modal-contact-create-accounting-entry"} solid={false} paymentMethod={method} setPaymentMethod={setMethod} setAccountingEntry={setAccountingEntry}></AppInputPaymentMethod>
                          </div>
                          <div className="col-lg-12 fv-row">
                            <label className="form-label required">{lang.labels.paymentDate}</label>
                            <input className="form-control" type="date" autoComplete="off" name="date" max={currentDate()} value={date.value.substring(0, 10)} onChange={handleChangeDate} />
                            <AppLegend component={"modal-contact-create-accounting-entry"} attribute={{check: datePayment(), name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className={`col-xl-6 ${!(subtype.valid && (subtype.value === "refund" || subtype.value === "payment")) && "opacity-50"}`} disabled={!(subtype.valid && (subtype.value === "refund" || subtype.value === "payment"))}>
                    <div className="h-100 m-0 ps-xl-5">
                      <div className="border border-dashed border-gray-300 rounded bg-lighten h-100 p-5">
                        <h6 className="text-dark text-center text-uppercase mb-3">{lang.labels.selectExistingTransaction}</h6>
                        <div className="fv-row mb-5">
                          <label className="form-label required">{lang.labels.transaction}</label>
                          <AppInputAccountingEntry component={"modal-contact-create-accounting-entry"} options={{solid: false, transaction: transaction}} companyForUser={companyForUser} accountingEntriesForCompany={accountingEntries()} treasuryAccountsForCompany={treasuryAccountsForCompany} accountingEntry={accountingEntry} setAccountingEntry={setAccountingEntry} setAmount={setAmount} setTreasuryAccount={setTreasuryAccount} setPaymentMethod={setMethod} setDate={setDate}></AppInputAccountingEntry>
                        </div>
                        <div className="table-responsive">
                          <table className={`table align-middle table-row-bordered ${!(subtype.valid && (subtype.value === "refund" || subtype.value === "payment")) ? "gy-2 mb-1" : "gy-3 mb-2"}`}>
                            <tbody>
                              <tr className="row-bordered">
                                <td className="text-secondary">{lang.labels.accountingAccount}</td>
                                <td className="fw-bold text-end">{accountingEntry.valid ? accountingAccountSelected(accountingAccountsForCompany, selectDebitCredit().id_account, companyForUser.language) : lang.labels.none}</td>
                              </tr>
                              <tr className="row-bordered d-none">
                                <td className="text-secondary">{lang.labels.paymentMethod}</td>
                                <td className="fw-bold text-end">{accountingEntry.valid ? paymentMethodValue(lang, selectDebitCredit().reference.payment_method) : lang.labels.none}</td>
                              </tr>
                              <tr className="row-bordered">
                                <td className="text-secondary">{lang.labels.totalDepositAmount}</td>
                                <td className="fw-bold text-end">{accountingEntry.valid ? `${companyForUser.currency?.symbol} ${formatMoney(selectDebitCredit().amount)}` : lang.labels.none}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>                
                    </div>
                  </fieldset>
                </div>
              </div >
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalPaymentCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountingEntryCreate}>
              <span className="indicator-label">{lang.labels.save}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalContactCreateAccountingEntry;
